/**
 * The account billing tab - current plan, account credit, add-ons and the plan catalogue.
 *
 * This file is the SHELL only: data loading, the Stripe/funnel return flows, the section
 * nav and the confirm dialogs. Everything that renders a card lives in `./billing/*`, which
 * is what keeps this file readable after the current-plan block grew a grouping model, a
 * quota meter and two disclosures.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertCircle, AlertTriangle, CheckCircle2, CreditCard, Layers, PackagePlus, Wallet,
} from "lucide-react";
import {
  Button, Card, CardContent, CardHeader, CardTitle, cn, ConfirmDialog, PlanComparison,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
  SectionNav, useScrollSpy, scrollToSection, type SectionNavItem, type AddOnTargetInput,
  LoadingState,
} from "@wa/ui";
import { useT, useI18n } from "../../i18n/I18nProvider";
import { ApiError } from "../../lib/api";
import { credit as creditApi, type AccountCredit } from "../../lib/credit";
import { keepPreview } from "../../lib/previewMode";
import {
  billing, accountSites, UNLIMITED,
  type BillingOverview, type BillingStatus, type AccountSitesOverview,
} from "../../lib/billing";
import { AccountPlanCard } from "./billing/AccountPlanCard";
import { AddOnsSection } from "./billing/AddOnsSection";
import { CreditWalletCard } from "./billing/CreditWalletCard";
import { groupSitesByAccount } from "./billing/lib";

type Cycle = "monthly" | "yearly";

export function BillingSection() {
  const t = useT();
  const { locale } = useI18n();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<BillingOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [cycle, setCycle] = useState<Cycle>("monthly");
  const [busy, setBusy] = useState<string | null>(null); // planCode currently in checkout
  // The account subscription's Stripe status (cancel/trial/refund) - a separate X-Site-Id request.
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [confirmAction, setConfirmAction] = useState<"cancel" | "refund" | null>(null);
  const [acting, setActing] = useState(false); // the confirm dialog's "working" state
  const [actionBusy, setActionBusy] = useState<string | null>(null); // "resume"
  // Quota downgrade: the account's sites + a picker dialog (the client chooses which survive).
  const [sitesData, setSitesData] = useState<AccountSitesOverview | null>(null);
  const [keepOpen, setKeepOpen] = useState(false);
  const [selectedKeep, setSelectedKeep] = useState<Set<string>>(new Set());
  const [keepBusy, setKeepBusy] = useState(false);
  // Keep the message's TRANSLATION KEY (not an already-translated string) so that the
  // message follows a language switch (otherwise it locks to the language at click time).
  const [notice, setNotice] = useState<{ kind: "success" | "error" | "info"; key: string } | null>(null);
  // Arrived through the public signup funnel (`?plan=<code>`) - main.tsx forwards the
  // signed-in user here; highlight the chosen plan + scroll to it.
  const [highlightPlan, setHighlightPlan] = useState<string | null>(null);
  // The wallet. Fetched here rather than inside the card because the nav item may only be
  // offered when there is a section to scroll to (the module being off answers 404).
  const [credit, setCredit] = useState<AccountCredit | null>(null);

  useEffect(() => {
    let alive = true;
    creditApi.summary()
      .then((c) => alive && setCredit(c))
      .catch(() => { /* wallet module off or no account -> 404 */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    billing
      .overview()
      .then((d) => {
        if (!alive) return;
        setData(d);
        // The cycle toggle starts on the current cycle -> the user's own plan card reads
        // "Current" (not a misleading "Switch to monthly") until they move the toggle.
        if (d.sites[0]?.billingCycle) setCycle(d.sites[0].billingCycle);
      })
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  // Stripe Checkout return status (?status=success|cancel) - the server redirects here
  // (`returnTo:"account"`). Show the message and clean the URL (keep the tab).
  useEffect(() => {
    const s = params.get("status");
    if (s === "success") setNotice({ kind: "success", key: "billing.checkoutSuccess" });
    else if (s === "cancel") setNotice({ kind: "info", key: "billing.checkoutCancel" });
    if (s) {
      params.delete("status");
      params.delete("session_id");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A plan carried over from the signup funnel (`?plan=`) -> highlight it (PlanComparison
  // scrolls to it) and clean the URL (keep the tab). Only once.
  useEffect(() => {
    const plan = (params.get("plan") ?? "").toLowerCase();
    if (!plan) return;
    setHighlightPlan(plan);
    params.delete("plan");
    params.delete("cycle");
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Billing is at the ACCOUNT level (one subscription covers every site of the account), but
  // `/billing/checkout` is site-scoped -> it needs an X-Site-Id. The first managed site
  // resolves the right account on the server. A user without a site cannot subscribe yet.
  const billingSiteId = data?.sites[0]?.siteId ?? null;

  // Load the subscription status + the account's sites (quota) once the billing site is known.
  useEffect(() => {
    if (!billingSiteId) return;
    let alive = true;
    billing.status(billingSiteId).then((s) => alive && setStatus(s)).catch(() => alive && setStatus(null));
    accountSites.overview(billingSiteId).then((d) => alive && setSitesData(d)).catch(() => alive && setSitesData(null));
    return () => { alive = false; };
  }, [billingSiteId]);

  // One card per billing account, not per site: sibling sites share a subscription, so the
  // old per-site card repeated the same plan, price, quota and 37 entitlement rows N times.
  const groups = useMemo(
    () => groupSitesByAccount(data?.sites ?? [], billingSiteId),
    [data, billingSiteId],
  );

  async function reload() {
    const [d, s, sites] = await Promise.all([
      billing.overview().catch(() => null),
      billingSiteId ? billing.status(billingSiteId).catch(() => null) : Promise.resolve(null),
      billingSiteId ? accountSites.overview(billingSiteId).catch(() => null) : Promise.resolve(null),
    ]);
    if (d) setData(d);
    if (s) setStatus(s);
    if (sites) setSitesData(sites);
  }

  /** Open the site picker (preselect the currently active ones, up to the quota). */
  function openKeepDialog() {
    if (!sitesData) return;
    const limit = sitesData.quota.limit;
    const active = sitesData.sites.filter((s) => s.status === "active").map((s) => s.id);
    setSelectedKeep(new Set(limit === UNLIMITED ? active : active.slice(0, limit)));
    setKeepOpen(true);
  }

  function toggleKeep(id: string) {
    setSelectedKeep((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function confirmKeep() {
    if (!billingSiteId) return;
    setKeepBusy(true);
    setNotice(null);
    try {
      await accountSites.keep(billingSiteId, [...selectedKeep]);
      setNotice({ kind: "success", key: "billing.keepDone" });
      setKeepOpen(false);
      await reload();
    } catch (e) {
      setNotice({ kind: "error", key: messageForError(e) });
    } finally {
      setKeepBusy(false);
    }
  }

  /** Cancel the subscription at period end (after the confirm dialog). */
  async function onCancelConfirmed() {
    if (!billingSiteId) return;
    setActing(true);
    setNotice(null);
    try {
      await billing.cancel(billingSiteId);
      setNotice({ kind: "success", key: "billing.cancelDone" });
      await reload();
    } catch (e) {
      setNotice({ kind: "error", key: messageForError(e) });
    } finally {
      setActing(false);
      setConfirmAction(null);
    }
  }

  /** Resume the subscription (undo a scheduled cancellation). */
  async function onResume() {
    if (!billingSiteId) return;
    setActionBusy("resume");
    setNotice(null);
    try {
      await billing.resume(billingSiteId);
      setNotice({ kind: "success", key: "billing.resumeDone" });
      await reload();
    } catch (e) {
      setNotice({ kind: "error", key: messageForError(e) });
    } finally {
      setActionBusy(null);
    }
  }

  /** Self-service refund (after the confirm dialog). */
  async function onRefundConfirmed() {
    if (!billingSiteId) return;
    setActing(true);
    setNotice(null);
    try {
      await billing.refund(billingSiteId);
      setNotice({ kind: "success", key: "billing.refundDone" });
      await reload();
    } catch (e) {
      setNotice({ kind: "error", key: messageForError(e) });
    } finally {
      setActing(false);
      setConfirmAction(null);
    }
  }

  async function onChoose(planCode: string, _kind?: string, opts?: { addOns?: AddOnTargetInput[] }) {
    if (!billingSiteId) return;
    setNotice(null);
    setBusy(planCode);
    try {
      // `addOns` = add-ons picked in the dialog (e.g. "+1 site", so the shrinking quota still
      // covers every site). They ride in the SAME switch call -> one proration, one invoice.
      const res = await billing.checkout(billingSiteId, planCode, cycle, opts?.addOns);
      if (res.url) {
        window.location.assign(res.url); // new subscription -> Stripe Checkout
      } else if (res.updated) {
        // An in-place plan change (proration) - no redirect. Refresh.
        setNotice({
          kind: "success",
          key:
            res.change === "downgrade"
              ? "billing.changeDowngraded"
              : res.change === "none"
                ? "billing.changeNoop"
                : "billing.changeUpgraded",
        });
        await reload();
      } else {
        setNotice({ kind: "error", key: "billing.genericError" });
      }
    } catch (e) {
      setNotice({ kind: "error", key: messageForError(e) });
    } finally {
      setBusy(null);
    }
  }

  // In-page section nav + scroll spy (click -> scroll; the active one is highlighted). The
  // credit entry only exists when the wallet does, so the nav and the spy ids are built
  // from ONE list - two hand-kept lists would drift and the highlight would stick.
  const navItems: SectionNavItem[] = [
    { id: "current", label: t("billing.sections.current"), icon: CreditCard },
    ...(credit ? [{ id: "credit", label: t("billing.sections.credit"), icon: Wallet }] : []),
    { id: "addons", label: t("billing.sections.addons"), icon: PackagePlus },
    { id: "plans", label: t("billing.sections.plans"), icon: Layers },
  ];
  const activeSection = useScrollSpy(navItems.map((i) => i.id));

  if (loading) {
    return (
      <Card>
        <CardContent><LoadingState label={t("common.loading")} /></CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">{t("billing.error")}</CardContent>
      </Card>
    );
  }

  // Billing is at the ACCOUNT level (one subscription covers every site of the account),
  // so the "current" plan for the comparison is the first managed site's.
  const currentCode = data.sites[0]?.planCode ?? "free";

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">{t("billing.title")}</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">{t("billing.subtitle")}</p>
      </div>

      {notice && (
        <div
          className={cn(
            "flex items-center gap-2 rounded-lg border p-3 text-sm",
            notice.kind === "success" && "border-success/30 bg-success/10 text-success",
            notice.kind === "error" && "border-destructive/30 bg-destructive/10 text-destructive",
            notice.kind === "info" && "border-border bg-muted text-muted-foreground",
          )}
        >
          {notice.kind === "success" ? <CheckCircle2 className="size-4 shrink-0" /> : <AlertCircle className="size-4 shrink-0" />}
          {t(notice.key)}
        </div>
      )}

      {/* Sticky section nav - REAL transparent frost: only `backdrop-blur`, WITHOUT a dark
          fill or gradient. The `mask` gradient fades the bottom edge, so scrolling content
          is frosted behind the tabs (readable) and melts softly into the page below - no
          dark box and no hard edge. Padding on top (pt-4) and bottom (pb-8). Full bleed
          (-mx-4). Parks under the global header (top-14); z-10 above the sections, below
          the header (z-20). `scroll-mt-36` keeps room on click. The mask only starts fading
          BELOW the buttons (64%), so the tab text stays sharp. */}
      <div
        className="sticky top-14 z-10 -mx-4 px-4 pt-4 pb-8 backdrop-blur"
        style={{
          maskImage: "linear-gradient(to bottom, black 64%, transparent)",
          WebkitMaskImage: "linear-gradient(to bottom, black 64%, transparent)",
        }}
      >
        <SectionNav items={navItems} activeId={activeSection} onSelect={scrollToSection} orientation="horizontal" />
      </div>

      {/* ── Current plan (one card per billing account) ─────────────────────── */}
      <section id="current" className="scroll-mt-36 space-y-4">
        {/* Quota downgrade: the client picks the sites that survive. */}
        {sitesData?.overQuota && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3.5">
            <div className="flex min-w-0 items-start gap-2 text-sm text-amber-800 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <span>
                {t("billing.overQuota", {
                  used: sitesData.sites.filter((s) => s.status === "active").length,
                  limit: sitesData.quota.limit,
                })}
                {sitesData.quotaEnforceAt && (
                  <> {t("billing.overQuotaBy", { date: new Date(sitesData.quotaEnforceAt).toLocaleDateString(locale === "et" ? "et-EE" : "en-US") })}</>
                )}
              </span>
            </div>
            <Button size="sm" onClick={openKeepDialog}>{t("billing.chooseSites")}</Button>
          </div>
        )}

        {/* Only a user managing sites across several accounts sees more than one card, and
            without this line two plan cards read like a bug. */}
        {groups.length > 1 && (
          <p className="text-sm text-muted-foreground">{t("billing.multipleAccounts")}</p>
        )}

        {groups.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <CreditCard className="mx-auto size-8 text-muted-foreground/50" />
              <p className="mt-3 text-sm font-medium text-foreground">{t("billing.noSites")}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t("billing.noSitesHint")}</p>
            </CardContent>
          </Card>
        ) : (
          groups.map((group) => (
            <AccountPlanCard
              key={group.key}
              group={group}
              catalog={data.catalog}
              status={status}
              locale={locale}
              t={t}
              resumeBusy={actionBusy === "resume"}
              onCancelClick={() => setConfirmAction("cancel")}
              onResumeClick={onResume}
              onRefundClick={() => setConfirmAction("refund")}
              onViewUsage={() => setParams(keepPreview({ tab: "usage" }))}
            />
          ))
        )}
      </section>

      {/* ── Account credit (wallet) + ledger ────────────────────────────────── */}
      {credit && (
        <section id="credit" className="scroll-mt-36">
          <CreditWalletCard credit={credit} locale={locale} t={t} />
        </section>
      )}

      {/* ── Add-ons (self-service extras) - buy/remove ──────────────────────── */}
      <section id="addons" className="scroll-mt-36">
        <AddOnsSection billingSiteId={billingSiteId} locale={locale} t={t} onChanged={reload} />
      </section>

      {/* ── Plan comparison (catalogue) + direct checkout/switch ────────────── */}
      <section id="plans" className="scroll-mt-36">
      {data.plans.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t("billing.comparePlans")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <PlanComparison
              plans={data.plans.map((p) => ({
                code: p.code,
                name: p.name,
                description: p.description,
                priceMonthlyCents: p.priceMonthlyCents,
                priceYearlyCents: p.priceYearlyCents,
                currency: p.currency,
                features: p.features ?? {},
                featured: p.featured,
                badge: p.badge,
                sortOrder: p.sortOrder ?? 0,
                trialDays: p.trialDays ?? 0,
              }))}
              catalog={data.catalog}
              currentCode={currentCode}
              currentCycle={data.sites[0]?.billingCycle ?? "monthly"}
              locale={locale === "et" ? "et" : "en"}
              cycle={cycle}
              onCycleChange={setCycle}
              onChoose={onChoose}
              onPreview={billingSiteId ? (planCode, bc, addOns) => billing.preview(billingSiteId, planCode, bc, addOns) : undefined}
              // The site picker is an account OWNER right (`/account/sites/keep` -> 403
              // otherwise), so a manager only gets the extra-site purchase in the dialog.
              onKeepSites={
                billingSiteId && sitesData?.isOwner
                  ? async (siteIds) => {
                      await accountSites.keep(billingSiteId, siteIds);
                      await reload();
                    }
                  : undefined
              }
              busyCode={busy}
              disabled={!billingSiteId}
              trialUsed={status?.trialUsed}
              highlightCode={highlightPlan ?? undefined}
            />
            <p className="text-xs text-muted-foreground">{t("billing.compareHint")}</p>
          </CardContent>
        </Card>
      )}
      </section>

      {/* Cancel / refund confirm dialogs. */}
      <ConfirmDialog
        open={confirmAction === "cancel"}
        title={t("billing.cancelConfirmTitle")}
        description={t("billing.cancelConfirmBody", {
          plan: currentCode,
          date: status?.currentPeriodEnd ? new Date(status.currentPeriodEnd).toLocaleDateString(locale === "et" ? "et-EE" : "en-US") : "-",
        })}
        confirmLabel={t("billing.cancelSub")}
        cancelLabel={t("common.cancel")}
        loading={acting}
        onConfirm={onCancelConfirmed}
        onCancel={() => setConfirmAction(null)}
      />
      <ConfirmDialog
        open={confirmAction === "refund"}
        title={t("billing.refundConfirmTitle")}
        description={t("billing.refundConfirmBody")}
        confirmLabel={t("billing.requestRefund")}
        cancelLabel={t("common.cancel")}
        loading={acting}
        onConfirm={onRefundConfirmed}
        onCancel={() => setConfirmAction(null)}
      />

      {/* Site picker (quota downgrade) - choose which survive, the rest are suspended. */}
      <Dialog open={keepOpen} onOpenChange={(o) => { if (!o && !keepBusy) setKeepOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("billing.chooseSitesTitle")}</DialogTitle>
            <DialogDescription>{t("billing.chooseSitesBody", { limit: sitesData?.quota.limit === UNLIMITED ? "∞" : (sitesData?.quota.limit ?? 0) })}</DialogDescription>
          </DialogHeader>
          <div className="max-h-72 space-y-1.5 overflow-y-auto py-1">
            {sitesData?.sites.map((s) => {
              const checked = selectedKeep.has(s.id);
              const limit = sitesData.quota.limit;
              const atLimit = limit !== UNLIMITED && selectedKeep.size >= limit && !checked;
              return (
                <label key={s.id} className={cn("flex items-center gap-3 rounded-lg border p-2.5 text-sm", checked ? "border-primary bg-primary/5" : "border-border", atLimit && "opacity-50")}>
                  <input type="checkbox" checked={checked} disabled={atLimit} onChange={() => toggleKeep(s.id)} className="size-4 accent-primary" />
                  <span className="min-w-0 flex-1 truncate">{s.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{s.host ?? s.slug}</span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <span className="mr-auto self-center text-xs text-muted-foreground">
              {t("billing.chooseSitesCount", { n: selectedKeep.size, limit: sitesData?.quota.limit === UNLIMITED ? "∞" : (sitesData?.quota.limit ?? 0) })}
            </span>
            <Button variant="outline" onClick={() => setKeepOpen(false)} disabled={keepBusy}>{t("common.cancel")}</Button>
            <Button
              onClick={() => void confirmKeep()}
              loading={keepBusy}
              disabled={selectedKeep.size === 0 || (sitesData?.quota.limit !== UNLIMITED && selectedKeep.size > (sitesData?.quota.limit ?? 0))}
            >
              {t("billing.chooseSitesConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Stripe checkout error code -> translation key (not a translated string, so the message follows the language). */
function messageForError(e: unknown): string {
  if (e instanceof ApiError) {
    const code = typeof e.body === "object" && e.body && "error" in e.body ? String((e.body as { error: unknown }).error) : "";
    if (code === "stripe_not_configured") return "billing.notConfigured";
    if (code === "plan_not_synced") return "billing.notSynced";
    if (code === "no_customer") return "billing.noCustomer";
  }
  return "billing.genericError";
}
